import { useQuery, useReactiveVar } from "@apollo/client";
import {
  GetOrganizationConnectedIntegrationTypesQuery,
  GetOrganizationConnectedIntegrationTypesQueryVariables,
  IntegrationTypes,
  LoggedInUserOrgFragment,
} from "types/graphql-schema";

import getOrganizationConnectedIntegrationTypesQuery from "@apps/programs/graphql/get-organization-connected-integration-types-query";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { role } from "@helpers/constants";

type Tool = {
  name: string;
  /** Connected for the whole organization, from the flags it already carries. */
  fromOrganization?: (organization: LoggedInUserOrgFragment) => boolean;
  /** Connected when the organization has connected an integration of this type. */
  integrationType?: IntegrationTypes;
};

/**
 * The tools Generate Context can draw on, by the category the integrations page
 * files them under. Only connections the organization owns count: a token one
 * person added for themselves gives nobody else any evidence.
 */
const readinessCategories: { label: string; tools: Tool[] }[] = [
  {
    label: "Messaging",
    tools: [
      {
        name: "Slack",
        fromOrganization: (organization) =>
          Boolean(organization.hasSlackConnection),
      },
      {
        name: "Teams",
        fromOrganization: (organization) =>
          Boolean(
            organization.hasTeamsInstallation ||
              organization.hasTeamsTranscriptConnection,
          ),
        integrationType: IntegrationTypes.Teams,
      },
    ],
  },
  {
    label: "Support + Engineering",
    tools: [
      {
        name: "GitHub",
        fromOrganization: (organization) =>
          Boolean(organization.hasGithubInstallation),
      },
      { name: "Linear", integrationType: IntegrationTypes.Linear },
      { name: "Jira", integrationType: IntegrationTypes.Jira },
      {
        name: "YouTrack",
        fromOrganization: (organization) =>
          Boolean(organization.hasYoutrackIntegration),
        integrationType: IntegrationTypes.Youtrack,
      },
    ],
  },
  {
    label: "CRM",
    tools: [
      { name: "Salesforce", integrationType: IntegrationTypes.Salesforce },
      { name: "HubSpot", integrationType: IntegrationTypes.Hubspot },
    ],
  },
  {
    label: "Productivity",
    tools: [
      {
        name: "Fellow",
        fromOrganization: (organization) =>
          Boolean(organization.hasFellowTranscriptSyncConnection),
      },
      { name: "Read AI", integrationType: IntegrationTypes.ReadAi },
    ],
  },
];

const canManageIntegrations = (organization: LoggedInUserOrgFragment) =>
  [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
    organization.userMembership?.role ?? "",
  );

const StatusPill = ({ connected }: { connected: boolean }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-sm ${
      connected
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-gray-200 bg-white text-gray-500"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500" : "bg-gray-300"}`}
    />
    {connected ? "Connected" : "Not connected"}
  </span>
);

/**
 * Which work tools the organization has connected, so an admin sees what
 * Generate Context will have to draw on before people start writing, and where
 * to connect more.
 */
const ReadinessIntegrations = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const organization = useReactiveVar(currentOrganizationVar);
  // Only the types, not the integration rows: the rows are for admins only, and
  // a manager setting up a review must see the same status as an admin.
  const { data } = useQuery<
    GetOrganizationConnectedIntegrationTypesQuery,
    GetOrganizationConnectedIntegrationTypesQueryVariables
  >(getOrganizationConnectedIntegrationTypesQuery, {
    variables: { organizationId },
  });

  const integrationTypes = new Set(
    data?.organization?.connectedIntegrationTypes,
  );
  const isConnected = (tool: Tool) =>
    Boolean(tool.fromOrganization?.(organization)) ||
    (tool.integrationType !== undefined &&
      integrationTypes.has(tool.integrationType));
  const canManage = canManageIntegrations(organization);

  return (
    <div className="mt-6 border-t pt-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        What people will have to draw on
      </div>
      <ul className="mt-2 divide-y">
        {readinessCategories.map((category) => {
          const connected = category.tools.some(isConnected);
          return (
            <li
              key={category.label}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5"
            >
              <span className="w-44 shrink-0 text-sm font-medium">
                {category.label}
              </span>
              <span className="min-w-0 flex-1 text-sm text-gray-400">
                {category.tools.map((tool, index) => (
                  <span key={tool.name}>
                    {index > 0 && " · "}
                    <span className={isConnected(tool) ? "text-gray-900" : ""}>
                      {tool.name}
                    </span>
                  </span>
                ))}
              </span>
              <StatusPill connected={connected} />
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-gray-50 px-4 py-3">
        {/* aggregate_context (chatbot/context_data.py) shares work tools and
            Slack only with the person and their managers; meetings go to
            anyone who can see them. */}
        <div className="text-sm text-gray-700">
          Generate Context draws on these tools in self and manager reviews.
          Peer reviewers get meeting notes only.
          {!canManage &&
            " An admin connects integrations on the Integrations settings page."}
        </div>
        {canManage && (
          <Button
            theme={buttonTheme.primary}
            text="Connect more integrations"
            to={`/settings/organization/${organizationId}/integrations`}
          />
        )}
      </div>
    </div>
  );
};

export default ReadinessIntegrations;
